import { intlFormat } from 'date-fns';
import { useIntl } from 'react-intl';

import { BasicInfo } from '@/src/listings/types';

const getLocation = (basicInfo: BasicInfo, venueName?: string): string | null => {
  const city = basicInfo.venue?.address?.city;
  const region = basicInfo.venue?.address?.region;
  let location: string | null;

  if (venueName && region) {
    if (city) {
      location = `${venueName}, ${city}, ${region}`;
    } else {
      location = `${venueName}, ${region}`;
    }
  } else if (venueName) {
    location = `${venueName}`;
  } else if (city && region) {
    location = `${city}, ${region}`;
  } else if (region) {
    location = `${region}`;
  } else {
    location = null;
  }
  return location;
};

export const useEventDescription = (basicInfo: BasicInfo | undefined, locale: string = 'en') => {
  const { formatMessage } = useIntl();

  if (!basicInfo) {
    return formatMessage({ id: 'Eventbrite - Find event and ticket information.' });
  }

  const organizationName = basicInfo.organizer.name;
  const venueName = basicInfo.isProtected ? '' : basicInfo.venue?.name;

  const date = basicInfo.startDate.local
    ? intlFormat(new Date(basicInfo.startDate.local), { dateStyle: 'full' }, { locale })
    : '';

  const location = getLocation(basicInfo, venueName);

  let description: string;

  // This function has a high cyclomatic complexity, but it cannot be easily refactored: to do so it should be split in many numerous hooks:
  // formatMessage cannot be passed as a reference into lower level functions: otherwise the parser does not detect the translations.
  // in and at must be translated and therefore must be in the main string.
  // the reduced complexity does not warrant the refactoring into many smaller hooks, hence leaving it as is.
  if (location && organizationName) {
    if (!venueName) {
      description = formatMessage(
        {
          id: 'Eventbrite - {organizationName} presents {eventName} - {date} in {location}. Find event and ticket information.',
          defaultMessage:
            'Eventbrite - {organizationName} presents {eventName} - {date} in {location}. Find event and ticket information.',
          description:
            "Example string is 'Eventbrite - Tortuga Music Festival presents Tortuga Music Festival - Wednesday, December 25, 2013 in San Francisco, CA. Find event and registration information.",
        },
        {
          organizationName,
          eventName: basicInfo.name,
          date,
          location,
        },
      );
    } else {
      description = formatMessage(
        {
          id: 'Eventbrite - {organizationName} presents {eventName} - {date} at {location}. Find event and ticket information.',
          defaultMessage:
            'Eventbrite - {organizationName} presents {eventName} - {date} at {location}. Find event and ticket information.',
          description:
            'Eventbrite - Tortuga Music Festival presents Tortuga Music Festival - Wednesday, December 25, 2013 at Venue, San Francisco, CA. Find event and registration information.',
        },
        {
          organizationName,
          eventName: basicInfo.name,
          date,
          location,
        },
      );
    }
  } else if (location) {
    if (!venueName) {
      description = formatMessage(
        {
          id: 'Eventbrite - {eventName} - {date} in {location}. Find event and ticket information.',
          defaultMessage: 'Eventbrite - {eventName} - {date} in {location}. Find event and ticket information.',
          description:
            'Eventbrite - Tortuga Music Festival - Wednesday, December 25, 2013 in San Francisco, CA. Find event and registration information.',
        },
        {
          eventName: basicInfo.name,
          date,
          location,
        },
      );
    } else {
      description = formatMessage(
        {
          id: 'Eventbrite - {eventName} - {date} at {location}. Find event and ticket information.',
          defaultMessage: 'Eventbrite - {eventName} - {date} at {location}. Find event and ticket information.',
          description:
            'Eventbrite - Tortuga Music Festival - Wednesday, December 25, 2013 at Venue, San Francisco, CA. Find event and registration information.',
        },
        {
          eventName: basicInfo.name,
          date,
          location,
        },
      );
    }
  } else if (organizationName) {
    description = formatMessage(
      {
        id: 'Eventbrite - {organizationName} presents {eventName} - {date} - Find event and ticket information.',
        defaultMessage:
          'Eventbrite - {organizationName} presents {eventName} - {date} - Find event and ticket information.',
        description:
          "Example string is 'Eventbrite - Tortuga Music Festival presents Tortuga Music Festival - Wednesday, December 25, 2013 - Find event and registration information.",
      },
      {
        organizationName,
        eventName: basicInfo.name,
        date,
      },
    );
  } else {
    description = formatMessage(
      {
        id: 'Eventbrite - {eventName} - {date} - Find event and ticket information.',
        defaultMessage: 'Eventbrite - {eventName} - {date} - Find event and ticket information.',
        description:
          'Eventbrite - Tortuga Music Festival - Wednesday, December 25, 2013. Find event and registration information.',
      },
      {
        eventName: basicInfo.name,
        date,
      },
    );
  }

  return description;
};
